// @flow

import React, { type Node } from 'react';

import { useUniqueId } from '@tectonics/hooks';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { InputMask } from '@tectonics/components/input-mask';
import { LATAM_PHONE_MASK } from '@tectonics/commons/masks.js';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';

import { TextField } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

export function FieldMexicanPhoneNumber(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextField('mexicanPhoneNumber');

  const validationError = useUserInputValidationError('mexicanPhoneNumber');
  const formHasBeenSent = useFormHasBeenSent();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('telephone');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="customer-phone-number">
      <label htmlFor={inputId}>Teléfono</label>

      <TextField
        as={InputMask}
        mask={LATAM_PHONE_MASK}
        type="tel"
        id={inputId}
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="address-phone-number"
        placeholder="Fijo o Celular"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          ¡Ojo! Es obligatorio informar un número de teléfono.
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          El número de teléfono que informaste no es válido.
        </ErrorLabel>
      );
  }
}
